import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAccountKey } from '../lib/account';
import { AppearanceControl } from '../lib/Appearance';
import { api } from '../lib/convex';
import { useCachedQuery } from '../lib/liveQuery';
import { LogoMark } from '../lib/LogoMark';
import { useOwnerJoinOffer } from '../lib/ownerJoin';
import { useCameraAvailable } from '../lib/qrScan';
import { ChevronRightIcon, ScanIcon } from '../launcher/icons';
import { SignInSheet, preloadSignInSheet, type SignInMode } from '../signin/SignInSheet';
import { PREVIEWS } from './AppPreviews';
import { Avatar } from './Avatar';
import { HeroVisual } from './HeroVisual';
import { InvitePaste } from './InvitePaste';
import s from './Landing.module.css';
import { solidTint } from './tint';

const STEPS: { title: string; body: ReactNode; art: ReactNode }[] = [
  {
    title: 'Create a circle from names',
    body: 'Add your people by name and give each a 4‑digit PIN. Kids too: no email, no password.',
    art: (
      <span className={s.artNames}>
        {['Anna', 'Alex', 'Maya'].map((name) => (
          <span key={name} className={s.chip}>
            <Avatar name={name} className={s.chipFace} />
            {name}
            {name === 'Maya' && <span className={s.kid}>kid</span>}
          </span>
        ))}
        <span className={s.chipAdd}>+ Add a person</span>
      </span>
    ),
  },
  {
    title: 'Share one invite link',
    body: 'They open it on their phone, tap their name and enter their PIN. Then they add the launcher to their home screen.',
    art: (
      <span className={s.artInvite}>
        <QrArt />
        <span className={s.inviteMain}>
          <span className={s.keyBox}>
            <span className={s.inviteUrl}>…/join/k7Qm2xPa</span>
            <span className={s.copy}>Copy link</span>
          </span>
          <span className={s.share}>Share the link</span>
        </span>
      </span>
    ),
  },
  {
    title: 'Send an app',
    body: (
      <>
        Describe it and the builder makes it, upload a folder, or run <code>circles send</code> from a terminal. It
        appears as a tile on every phone.
      </>
    ),
    art: (
      <span className={s.artComposer}>
        <span className={s.composerText}>A chore chart with pocket money for the kids</span>
        <span className={s.composerFoot}>
          <span className={s.composerTarget}>
            <LogoMark size={16} />
            Family
          </span>
          <span className={s.composerBuild}>Build</span>
        </span>
      </span>
    ),
  },
];

const EXAMPLES = [
  { slug: 'chores', icon: '🧹', name: 'Chores', line: 'Who does what this week. Kids tick chores off, adults hear about it and pay out pocket money.' },
  { slug: 'shopping', icon: '🛒', name: 'Shopping', line: 'One list for the house, private notes for you, and “I’m going shopping” tells the others.' },
  { slug: 'hello', icon: '👋', name: 'Hello', line: 'Wave at each other and watch the waves arrive live. A good first app to send.' },
];

const PROMISES = [
  { title: 'No accounts for them', body: 'Nobody signs up. Each person is a name and a PIN that you set.' },
  { title: 'One link to join', body: 'Send it in any chat, or let them scan its QR code from your screen.' },
  { title: 'No app store', body: 'The launcher installs from the browser. Every app you send after that just appears in it.' },
  { title: 'Private to the circle', body: 'Apps open only on your members’ phones, and each circle’s data stays in that circle.' },
  { title: 'Quiet nights for kids', body: 'Members marked as kids get no notifications between 21:00 and 07:00.' },
  { title: 'Revoke any phone', body: 'Lost a phone? Sign it out from the builder and it loses access at once.' },
];

const FAQ: { q: string; a: ReactNode }[] = [
  {
    q: 'Do my family need accounts?',
    a: 'No. Each person in a circle is a name and a 4‑digit PIN that you set. They open the invite link on their phone, tap their name and enter the PIN: no email, no password, no app store. Only you, the builder, have a key, and you can add a recovery email or a passkey to it.',
  },
  {
    q: 'What exactly is a circle?',
    a: 'A small group of named people, like a family or a few friends. You create it once from their names. Every app you send goes to the whole circle: it knows who is in it, keeps shared and private data for them, and can notify them. That data stays in the circle.',
  },
  {
    q: 'Do I have to use the builder agent?',
    a: (
      <>
        No. Describing an app to the builder is one way. You can also upload a folder with your own HTML, JavaScript and a{' '}
        <code>circle.json</code>, or run <code>circles send</code> from a terminal. All three end the same way: a new tile on
        every member’s phone.
      </>
    ),
  },
  {
    q: 'What happens if a phone is lost?',
    a: 'Open the circle in the builder and revoke that phone. It loses access at once: the apps stop working there and it gets no more notifications. The person can join again on a new phone with the invite link and their PIN.',
  },
];

/** What opens the sign-in sheet; the element that did gets focus back when it closes. */
type OpenSheet = (mode: SignInMode, e: MouseEvent<HTMLElement>) => void;

/**
 * "/" on a browser without a device token. Public: what Bolt Circles is, with the two doors (build,
 * join) in the hero; "Start building" and "Sign in" open the sign-in sheet (architecture §14.77).
 * With a builder key on this browser (owner mode): the owner's circles first, then the same doors.
 *
 * `gate`: rendered by the builder at `/builder/*` without a key. The sheet opens at once in sign-in
 * mode and the URL stays, so a sign-in lands on the page that was asked for; closing it goes to `/`.
 */
export function Landing({ notice, gate = false }: { notice?: ReactNode; gate?: boolean }) {
  const accountKey = useAccountKey();
  const owner = accountKey !== null;
  const [top, scrolled] = useScrolled();
  const navigate = useNavigate();
  const [sheet, setSheet] = useState<SignInMode | null>(gate ? 'signin' : null);
  const opener = useRef<HTMLElement | null>(null);

  const open: OpenSheet = (mode, e) => {
    opener.current = e.currentTarget;
    setSheet(mode);
  };
  const close = () => {
    setSheet(null);
    if (gate) navigate('/', { replace: true });
    else opener.current?.focus();
  };
  // The key is stored: at "/" the builder is next; under /builder/* the builder re-renders in place.
  const signedIn = () => {
    setSheet(null);
    if (!gate) navigate('/builder');
  };

  return (
    <div className={s.landing} data-testid={owner ? 'landing-owner' : 'landing-public'}>
      <div ref={top} className={s.sentinel} aria-hidden />
      <div className={s.backdrop} aria-hidden>
        <span className={s.glowSun} />
        <span className={s.glowPeach} />
        <span className={s.glowSage} />
      </div>

      <header className={scrolled ? `${s.header} ${s.scrolled}` : s.header}>
        <div className={`${s.wrap} ${s.headerInner}`}>
          <Link to="/" className={s.wordmark}>
            <LogoMark size={32} />
            <span>Bolt Circles</span>
          </Link>
          {!owner && (
            <button
              type="button"
              className={s.headerLink}
              onClick={(e) => open('signin', e)}
              onPointerEnter={preloadSignInSheet}
              onFocus={preloadSignInSheet}
            >
              Sign in
            </button>
          )}
        </div>
      </header>

      <main className={s.main}>
        {notice && <div className={s.wrap}>{notice}</div>}
        {owner ? <OwnerHome accountKey={accountKey} /> : <PublicHome open={open} />}
      </main>

      {!owner && <SignInSheet open={sheet !== null} mode={sheet ?? 'signin'} onClose={close} onSignedIn={signedIn} />}

      <footer className={s.footer}>
        <div className={`${s.wrap} ${s.footerInner}`}>
          <div className={s.footerBrand}>
            <LogoMark size={24} />
            <p className={s.credit}>Bolt Circles · a StackBlitz Labs prototype</p>
          </div>
          <nav className={s.footerLinks} aria-label="Footer">
            {owner ? (
              <Link to="/builder" className={s.footerLink}>
                Open the builder
              </Link>
            ) : (
              // Without a key the builder is behind the sheet; open it here instead of at /builder.
              <button
                type="button"
                className={s.footerLink}
                onClick={(e) => open('signin', e)}
                onPointerEnter={preloadSignInSheet}
                onFocus={preloadSignInSheet}
              >
                Open the builder
              </button>
            )}
            <a href="#door-invited" className={s.footerLink} onClick={goToInviteDoor}>
              I was invited
            </a>
          </nav>
          <AppearanceControl size="sm" className={s.appearance} />
        </div>
      </footer>
    </div>
  );
}

/** True once the page has scrolled past its top few pixels (the header turns translucent). */
function useScrolled() {
  const ref = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => entry && setScrolled(!entry.isIntersecting));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, scrolled] as const;
}

function goToInviteDoor(e: MouseEvent<HTMLAnchorElement>) {
  const input = document.getElementById('invite');
  if (!input) return;
  e.preventDefault();
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  input.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
  input.focus({ preventScroll: true });
}

function PublicHome({ open }: { open: OpenSheet }) {
  return (
    <>
      <section className={`${s.wrap} ${s.hero}`} aria-labelledby="hero-title">
        <div className={s.heroCopy}>
          <h1 id="hero-title" className={s.headline}>
            Little apps for your people.
          </h1>
          <p className={s.lead}>
            Describe an app, send it, and it shows up on everyone’s phones. No app store and no accounts for them — just
            one link, their name, and a PIN.
          </p>
          <div className={s.heroDoors}>
            <BuildDoor open={open} />
            <InvitedDoor plain={false} />
          </div>
        </div>
        <HeroVisual />
      </section>

      <section className={s.band} aria-labelledby="how-it-works">
        <div className={s.wrap}>
          <h2 id="how-it-works" className={s.sectionTitle}>
            How it works
          </h2>
          <ol className={s.steps}>
            {STEPS.map((step, i) => (
              <li key={step.title} className={s.step}>
                <span className={s.stepArt} aria-hidden>
                  {step.art}
                </span>
                <h3 className={s.stepTitle}>
                  <span className={s.stepNumber} aria-hidden>
                    {i + 1}
                  </span>
                  {step.title}
                </h3>
                <p className={s.stepBody}>{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className={`${s.wrap} ${s.section}`} aria-labelledby="examples">
        <h2 id="examples" className={s.sectionTitle}>
          What people have made
        </h2>
        <ul className={s.examples}>
          {EXAMPLES.map((ex) => (
            <li key={ex.name} className={s.example}>
              <div className={s.preview} aria-hidden>
                {PREVIEWS[ex.slug]}
              </div>
              <span className={s.exampleIcon} style={solidTint(ex.slug)} aria-hidden>
                {ex.icon}
              </span>
              <div className={s.exampleText}>
                <h3 className={s.exampleName}>{ex.name}</h3>
                <p className={s.exampleLine}>{ex.line}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className={`${s.wrap} ${s.section}`} aria-labelledby="promises">
        <div className={s.promisePanel}>
          <h2 id="promises" className={s.sectionTitle}>
            Simple for them, private to you
          </h2>
          <ul className={s.promises}>
            {PROMISES.map((p) => (
              <li key={p.title} className={s.promise}>
                <h3 className={s.promiseTitle}>{p.title}</h3>
                <p className={s.promiseBody}>{p.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className={`${s.wrap} ${s.section} ${s.faq}`} aria-labelledby="faq">
        <h2 id="faq" className={s.sectionTitle}>
          Questions
        </h2>
        <div className={s.faqList}>
          {FAQ.map((item) => (
            <details key={item.q} className={s.faqItem}>
              <summary className={s.faqQ}>
                {item.q}
                <span className={s.faqIcon} aria-hidden />
              </summary>
              <p className={s.faqA}>{item.a}</p>
            </details>
          ))}
        </div>
      </section>
    </>
  );
}

/** A made-up QR code (finder squares + a fixed pattern) for the invite card drawing. */
function QrArt() {
  return (
    <svg className={s.qr} viewBox="0 0 25 25" aria-hidden>
      <rect width="25" height="25" rx="2.5" fill="#fff" />
      <path d={QR_PATH} fill="#29251f" />
    </svg>
  );
}

const QR_PATH = (() => {
  const finder = (x: number, y: number) =>
    `M${x} ${y}h7v7h-7zM${x + 1} ${y + 1}v5h5v-5zM${x + 2} ${y + 2}h3v3h-3z`;
  let d = finder(2, 2) + finder(16, 2) + finder(2, 16);
  let seed = 7;
  for (let y = 2; y < 23; y++) {
    for (let x = 2; x < 23; x++) {
      const inFinder = (x < 10 && y < 10) || (x > 14 && y < 10) || (x < 10 && y > 14);
      seed = (seed * 1103515245 + 12345) % 2147483648;
      if (!inFinder && seed % 100 < 46) d += `M${x} ${y}h1v1h-1z`;
    }
  }
  return d;
})();

/** Public: "Start building" opens the sheet ready to create an account. Owner mode: a link to the builder. */
function BuildDoor({ open }: { open?: OpenSheet }) {
  const owner = open === undefined;
  return (
    <section className={owner ? s.doorPlain : s.doorBuild} aria-labelledby="door-build" data-testid="door-build">
      <h2 id="door-build" className={s.doorTitle}>
        {owner ? 'Start a new circle' : 'I want to build for my people'}
      </h2>
      <p className={s.doorText}>
        {owner
          ? 'A circle is a name and the names of its people. The apps you send to it stay in it.'
          : 'Create a circle from names, then describe an app to the builder or upload your own.'}
      </p>
      {open ? (
        <button
          type="button"
          className={`${s.primary} ${s.big} ${s.doorAction}`}
          onClick={(e) => open('create', e)}
          onPointerEnter={preloadSignInSheet}
          onFocus={preloadSignInSheet}
        >
          Start building
        </button>
      ) : (
        <Link to="/builder" className={`${s.secondary} ${s.doorAction}`}>
          Open the builder
        </Link>
      )}
    </section>
  );
}

function InvitedDoor({ plain }: { plain: boolean }) {
  const camera = useCameraAvailable();
  return (
    <section className={plain ? s.doorPlain : s.doorInvited} aria-labelledby="door-invited" data-testid="door-invited">
      <h2 id="door-invited" className={s.doorTitle}>
        I was invited
      </h2>
      <p className={s.doorText}>Open the link you were sent on the phone you want to use. It asks which one you are and for your PIN.</p>
      <InvitePaste />
      {camera && (
        <Link to="/join/scan" className={s.scan}>
          <ScanIcon size={20} /> Scan a QR code
        </Link>
      )}
    </section>
  );
}

function OwnerHome({ accountKey }: { accountKey: string }) {
  return (
    <div className={`${s.wrap} ${s.ownerHome}`}>
      <OwnerPanel accountKey={accountKey} />
      <div className={s.ownerDoors}>
        <InvitedDoor plain />
        <BuildDoor />
      </div>
    </div>
  );
}

function OwnerPanel({ accountKey }: { accountKey: string }) {
  const { data: circles } = useCachedQuery(api.circles.list, { accountKey });
  const sorted = circles ? [...circles].sort((a, b) => b.createdAt - a.createdAt) : undefined;
  const offer = useOwnerJoinOffer(accountKey);

  return (
    <section className={s.owner} aria-labelledby="owner-title" data-testid="owner-panel">
      <div className={s.ownerHead}>
        <div>
          <h1 id="owner-title" className={s.ownerTitle}>
            Your circles
          </h1>
          <p className={s.ownerLead}>Open one to add people, share the invite link or send an app.</p>
        </div>
        <Link to="/builder" className={`${s.primary} ${s.ownerAction}`}>
          Your builder
        </Link>
      </div>
      {sorted === undefined ? (
        <ul className={s.circleList} aria-busy="true" aria-label="Loading your circles" data-testid="owner-circles-skeleton">
          {[0, 1].map((i) => (
            <li key={i} className={s.circleSkeleton} aria-hidden>
              <span className={`${s.skeleton} ${s.circleAvatar}`} />
              <span className={s.circleText}>
                <span className={s.skeleton} style={{ width: '58%', height: 16 }} />
                <span className={s.skeleton} style={{ width: '82%', height: 12 }} />
              </span>
            </li>
          ))}
        </ul>
      ) : sorted.length === 0 ? (
        <div className={s.ownerEmpty}>
          <LogoMark size={40} />
          <p>No circles yet. Create one in the builder: a name for the circle, then the names of your people.</p>
        </div>
      ) : (
        <ul className={s.circleList} data-testid="owner-circles">
          {sorted.map((c) => (
            <li key={c._id}>
              <Link to={`/builder/${c.slug}`} className={s.circleLink}>
                <span className={s.circleAvatar} style={solidTint(c._id, 1.4)} aria-hidden>
                  {(Array.from(c.name.trim())[0] ?? '?').toUpperCase()}
                </span>
                <span className={s.circleText}>
                  <span className={s.circleName}>{c.name}</span>
                  <span className={s.circleMeta}>
                    {c.memberCount} {c.memberCount === 1 ? 'member' : 'members'} · {c.appCount} {c.appCount === 1 ? 'app' : 'apps'}
                  </span>
                </span>
                <ChevronRightIcon size={18} className={s.circleChevron} />
              </Link>
            </li>
          ))}
        </ul>
      )}
      {offer.circles.length > 0 && (
        <div className={s.ownerJoin} data-testid="owner-join">
          <p className={s.ownerJoinText}>
            <strong>Use your apps here too.</strong> Your builder key lets this device join as you, no PIN needed.
          </p>
          <button className={`${s.secondary} ${s.wrapText}`} disabled={offer.busy} onClick={() => void offer.join()}>
            {offer.busy ? 'Joining…' : offer.label}
          </button>
          {offer.error && <p className={s.pasteError}>{offer.error}</p>}
        </div>
      )}
    </section>
  );
}
